/**
 * The five loyalty tier bands -- phase 6b, Task 5.
 *
 * GET/PUT /admin/discounts/loyalty/tiers. One table, five rows, ALL five
 * submitted on Save (the API replaces the set atomically). The bands are
 * half-open `[min, max)` and contiguous, so:
 * - min is read-only and derived: tier 1 is 0.00 (ruling A-1), tier n+1 is a
 *   string COPY of tier n's max (`relink` in tiers.ts -- no arithmetic);
 * - max is editable on tiers 1-4 and "∞" on tier 5 (submitted as null);
 * - `active` is a read-only chip (ruling A-7: the API refuses active:false
 *   because customers in a switched-off band would have no tier), and every
 *   row is submitted with active: true;
 * - `promotionDiscountValue` must equal `rewardAmount`; a drift chip flags a
 *   row where it does not (saving rewrites both in one transaction).
 *
 * Errors: `validateTiers` (the API's refine, run locally) and a 422's
 * `fieldErrors` both key `tiers.<i>.<field>` on the array sorted by
 * tierNumber, so index i is row tierNumber i+1.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { discountsApi, qk } from '../api';
import { toApiFailure } from '../errors';
import { formatRand, toMoneyString } from '../money';
import type { TierInput, TierRow } from '../types';
import { hasDrift, relink, sortTiers, TIER_COUNT, toTierInputs, validateTiers } from './tiers';

export const DRIFT_LABEL = 'value out of sync — saving will fix';

type MoneyField = 'minLifetimeSpend' | 'maxLifetimeSpend' | 'rewardAmount';

/**
 * Normalise every money cell to the API's 2dp string, re-link, and run the
 * local refine. Returns the body to PUT or the cell errors that stop it.
 */
export function prepareTiers(draft: TierInput[]): { tiers?: TierInput[]; errors: Record<string, string> } {
  const errors: Record<string, string> = {};
  const normalised = sortTiers(draft).map((t, i) => {
    const money = (field: MoneyField, raw: string | null, required: boolean): string | null => {
      if (raw === null) return null;
      try {
        const v = toMoneyString(raw);
        if (v === null && required) errors[`tiers.${i}.${field}`] = 'Required';
        return v;
      } catch (e) {
        errors[`tiers.${i}.${field}`] = (e as Error).message;
        return raw;
      }
    };
    return {
      ...t,
      minLifetimeSpend: money('minLifetimeSpend', t.minLifetimeSpend, true) ?? t.minLifetimeSpend,
      maxLifetimeSpend: i === TIER_COUNT - 1 ? null : money('maxLifetimeSpend', t.maxLifetimeSpend, true),
      rewardAmount: money('rewardAmount', t.rewardAmount, true) ?? t.rewardAmount,
      active: true,
    };
  });
  if (Object.keys(errors).length) return { errors };
  const tiers = relink(normalised);
  const refine = validateTiers(tiers);
  if (Object.keys(refine).length) return { errors: refine };
  return { tiers, errors };
}

const cellInput = (readOnly: boolean, label: string, extra: Record<string, unknown> = {}) => ({
  input: { readOnly },
  htmlInput: { 'aria-label': label, inputMode: 'decimal' as const, ...extra },
});

const TierTable: React.FC<{ rows: TierRow[]; readOnly: boolean }> = ({ rows, readOnly }) => {
  const notify = useNotify();
  const qc = useQueryClient();
  const sorted = sortTiers(rows);
  const [draft, setDraft] = useState<TierInput[]>(() => relink(toTierInputs(rows)));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: discountsApi.putTiers,
    onSuccess: () => {
      setFieldErrors({});
      setFormError(null);
      notify('Tier bands saved.', { type: 'success' });
      qc.invalidateQueries({ queryKey: qk.tiers });
      // rewardAmount is also the tier promotion's discountValue.
      qc.invalidateQueries({ queryKey: ['discounts', 'promotions'] });
    },
    onError: (err) => {
      const failure = toApiFailure(err);
      if (failure.status === 422) {
        setFieldErrors(failure.fieldErrors);
        setFormError(Object.keys(failure.fieldErrors).length ? null : failure.message);
      } else {
        notify(failure.message, { type: 'error' });
      }
    },
  });

  const errorAt = (tierNumber: number, field: string): string => fieldErrors[`tiers.${tierNumber - 1}.${field}`] ?? '';

  const clearErrorAt = (tierNumber: number, field: string) =>
    setFieldErrors((f) => {
      const key = `tiers.${tierNumber - 1}.${field}`;
      if (!f[key]) return f;
      const rest = { ...f };
      delete rest[key];
      return rest;
    });

  /** Set one cell and re-derive the linked mins (max -> next min is a string copy). */
  const setField = (tierNumber: number, patch: Partial<TierInput>) => {
    setDraft((d) => relink(d.map((t) => (t.tierNumber === tierNumber ? { ...t, ...patch } : t))));
  };

  /** On blur, swap what was typed for the API's 2dp form (if it parses) so the derived min shows it too. */
  const normaliseOnBlur = (tierNumber: number, field: 'maxLifetimeSpend' | 'rewardAmount', raw: string | null) => {
    if (raw === null) return;
    try {
      const v = toMoneyString(raw);
      if (v !== null && v !== raw) setField(tierNumber, { [field]: v });
    } catch {
      // left as typed; Save reports it on the cell
    }
  };

  const onSave = () => {
    const { tiers, errors } = prepareTiers(draft);
    setFieldErrors(errors);
    setFormError(null);
    if (tiers) {
      setDraft(tiers);
      save.mutate({ tiers });
    }
  };

  return (
    <Stack spacing={2} data-testid="loyalty-tiers-form">
      {formError && <Alert severity="error">{formError}</Alert>}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label="Loyalty tier bands">
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              <TableCell>Promotion</TableCell>
              <TableCell>From (R)</TableCell>
              <TableCell>Up to (R)</TableCell>
              <TableCell>Reward value (R)</TableCell>
              <TableCell>Daily redemption limit</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {draft.map((t, i) => {
              const row = sorted[i];
              const isLast = i === TIER_COUNT - 1;
              const drift = row ? hasDrift(row) : false;
              return (
                <TableRow key={t.tierNumber} data-testid={`tier-row-${t.tierNumber}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography fontWeight={700}>Tier {t.tierNumber}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row?.promotionName ?? '—'}</Typography>
                    <Typography variant="caption" color={drift ? 'warning.main' : 'text.secondary'}>
                      currently {formatRand(row?.promotionDiscountValue)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={t.minLifetimeSpend}
                      error={Boolean(errorAt(t.tierNumber, 'minLifetimeSpend'))}
                      helperText={errorAt(t.tierNumber, 'minLifetimeSpend') || (i === 0 ? 'always 0' : 'derived')}
                      slotProps={cellInput(true, `Tier ${t.tierNumber} min`)}
                    />
                  </TableCell>
                  <TableCell>
                    {isLast ? (
                      <Typography variant="h6" component="span" aria-label={`Tier ${t.tierNumber} max`}>
                        ∞
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        value={t.maxLifetimeSpend ?? ''}
                        onChange={(e) => {
                          clearErrorAt(t.tierNumber, 'maxLifetimeSpend');
                          clearErrorAt(t.tierNumber + 1, 'minLifetimeSpend');
                          setField(t.tierNumber, { maxLifetimeSpend: e.target.value });
                        }}
                        onBlur={() => normaliseOnBlur(t.tierNumber, 'maxLifetimeSpend', t.maxLifetimeSpend)}
                        error={Boolean(errorAt(t.tierNumber, 'maxLifetimeSpend'))}
                        helperText={errorAt(t.tierNumber, 'maxLifetimeSpend') || ' '}
                        slotProps={cellInput(readOnly, `Tier ${t.tierNumber} max`)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={t.rewardAmount}
                      onChange={(e) => {
                        clearErrorAt(t.tierNumber, 'rewardAmount');
                        setField(t.tierNumber, { rewardAmount: e.target.value });
                      }}
                      onBlur={() => normaliseOnBlur(t.tierNumber, 'rewardAmount', t.rewardAmount)}
                      error={Boolean(errorAt(t.tierNumber, 'rewardAmount'))}
                      helperText={errorAt(t.tierNumber, 'rewardAmount') || ' '}
                      slotProps={cellInput(readOnly, `Tier ${t.tierNumber} reward`)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      value={t.dailyRedemptionLimit ?? ''}
                      onChange={(e) => {
                        clearErrorAt(t.tierNumber, 'dailyRedemptionLimit');
                        const v = e.target.value.trim();
                        // Number(), not parseInt(): "1.5" must reach validateTiers as 1.5 and be
                        // refused as not a whole number, never silently truncated to 1.
                        setField(t.tierNumber, { dailyRedemptionLimit: v === '' ? null : Number(v) });
                      }}
                      error={Boolean(errorAt(t.tierNumber, 'dailyRedemptionLimit'))}
                      helperText={errorAt(t.tierNumber, 'dailyRedemptionLimit') || 'blank = unlimited'}
                      slotProps={{ input: { readOnly }, htmlInput: { 'aria-label': `Tier ${t.tierNumber} daily limit`, min: 1, step: 1 } }}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Tooltip title="A band cannot be switched off — customers in it would have no tier. Move the boundaries instead.">
                        <Chip size="small" color="success" variant="outlined" label={t.active ? 'Active' : 'Inactive'} />
                      </Tooltip>
                      {drift && (
                        <Tooltip title={`Promotion value ${formatRand(row?.promotionDiscountValue)} ≠ reward ${formatRand(row?.rewardAmount)}`}>
                          <Chip size="small" color="warning" label={DRIFT_LABEL} />
                        </Tooltip>
                      )}
                      {errorAt(t.tierNumber, 'active') && (
                        <Typography variant="caption" color="error">
                          {errorAt(t.tierNumber, 'active')}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Bands are [from, up to) on lifetime spend; each band starts where the previous one ends. Last updated{' '}
          {new Date(sorted.reduce((m, r) => (r.updatedAt > m ? r.updatedAt : m), sorted[0]?.updatedAt ?? '')).toLocaleString()}.
        </Typography>
        {!readOnly && (
          <Button variant="contained" onClick={onSave} disabled={save.isPending}>
            Save tier bands
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export const TierBandsEditor: React.FC<{ readOnly: boolean }> = ({ readOnly }) => {
  const { data, isLoading, error } = useQuery({ queryKey: qk.tiers, queryFn: discountsApi.tiers });

  if (error) {
    return <Alert severity="error">Could not load tier bands: {toApiFailure(error).message}</Alert>;
  }
  if (isLoading || !data) {
    return <Skeleton variant="rounded" height={320} data-testid="loyalty-tiers-loading" />;
  }
  if (data.tiers.length !== TIER_COUNT) {
    return (
      <Alert severity="warning">
        Expected {TIER_COUNT} tier bands but the API returned {data.tiers.length}. The bands are misconfigured; this needs the backend seed
        before they can be edited here.
      </Alert>
    );
  }
  // Keyed on the rows' updatedAt so a saved set replaces the draft, while a
  // refetch of unchanged rows leaves in-progress edits alone.
  const key = sortTiers(data.tiers)
    .map((t) => `${t.id}@${t.updatedAt}`)
    .join('|');
  return <TierTable key={key} rows={data.tiers} readOnly={readOnly} />;
};

export default TierBandsEditor;
